import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import _isNil from 'lodash/isNil';
import Message from 'components/Message';
import Dot from 'components/Dot';
import { bem } from 'helpers/class-name';
import { selectActiveVenue } from 'selectors/general';
import EnableClassSwitch from '../EnableClassSwitch/EnableClassSwitch';
import BookableStatus from '../BookableStatus/BookableStatus';
import messages from '../ClassesList.messages';
import { getVenueClassPayoutRate } from '../ClassesList.helpers';

import './ClassesListRow.scss';

const { block, element } = bem('classes-list-row');

const ClassesListRow = ({
  classTitleLink,
  showEnableClassToggle,
  showPricingColumns,
  updateIsClassEnabled,
  venueClass,
}) => {
  const { currencyCode } = useSelector(selectActiveVenue);
  const { classId, className, disabled } = venueClass;
  const payoutRate = getVenueClassPayoutRate(venueClass, currencyCode);
  const hasPayout = !_isNil(payoutRate);

  const renderBookableContent = () => {
    if (showPricingColumns) {
      return <BookableStatus venueClass={venueClass} />;
    }

    if (showEnableClassToggle) {
      return (
        <EnableClassSwitch disabled={disabled} updateIsClassEnabled={updateIsClassEnabled} venueClass={venueClass} />
      );
    }

    return (
      <>
        <Dot className="ml-small" color={!disabled ? Dot.COLORS.GREEN : Dot.COLORS.RED} />
        <Message className="ml-small" message={!disabled ? messages.enabled : messages.disabled} />
      </>
    );
  };

  return (
    <div className={block('grid grid--middle')} data-cypress="classes-detail-row">
      <div className={element('item', 'grid__item 1/2')}>
        <div className={element('cell', { extended: true })}>
          {!classTitleLink ? (
            className
          ) : (
            <Link to={classTitleLink(classId)} data-cypress="class-detail-row-link">
              <span className={element('link')}>{className}</span>
            </Link>
          )}
        </div>
      </div>
      {showPricingColumns && (
        <div className={element('item', 'grid__item 1/6', 'text--right')}>
          <div className={element('cell', 'payout')}>
            {hasPayout ? <Message format="currency" currency={currencyCode} value={payoutRate} /> : '––'}
          </div>
        </div>
      )}
      <div
        className={element(
          'item',
          showPricingColumns ? 'grid__item 1/3 text--right pr-large' : 'grid__item 1/2 text--right pr-large'
        )}>
        {renderBookableContent()}
      </div>
    </div>
  );
};

ClassesListRow.propTypes = {
  venueClass: PropTypes.shape({
    classId: PropTypes.number,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    payout: PropTypes.shape({
      minimumPayout: PropTypes.shape({
        currencyCode: PropTypes.string,
        currencyUnits: PropTypes.number,
      }),
    }),
    retailRateRequest: PropTypes.shape({
      status: PropTypes.string,
      statusReason: PropTypes.string,
    }),
  }),
  classTitleLink: PropTypes.func,
  showEnableClassToggle: PropTypes.bool,
  showPricingColumns: PropTypes.bool,
  updateIsClassEnabled: PropTypes.func,
};

export default ClassesListRow;
